import { Link, usePage } from "@inertiajs/react"
import {
  LayoutDashboard,
  FileText,
  BookOpen,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ExternalLink,
  Users,
  Shield,
  Lock,
  BookMarked,
} from "lucide-react"
import { useState, useEffect } from "react"
import { useMediaQuery } from "@/hooks/use-media-query"
import { inertiaUrlPathname, inertiaUrlTab } from "@/lib/utils"
import { CRM_APP_URL } from "@/lib/kf-crm"
import { Separator } from "@/components/ui/separator"
import logoBlackText from "@/assets/logos/kf-logo-black-text.webp"
import FeedbackWidget from "@/components/feedback/FeedbackWidget"
import { ImpersonationBanner } from "@/components/ImpersonationBanner"
import { TourProvider, useTour } from "@/components/tour/TourContext"
import CoachTour from "@/components/tour/CoachTour"

// Top-level groups mirror the Admin sidebar pattern: a few broad categories,
// each expanding to its sub-pages via `subItems`. SectionTabs renders the
// sub-items as horizontal tabs at the top of each page.
//
// `betaOnly: true` (top-level or sub-item) → hidden from non-beta coaches.
// `coachOnly: true` → hidden from authors and admins who are not coaches.
// Controller-side RequireBetaTester remains the source of truth; this filter
// keeps the sidebar honest. Admins are backfilled to is_beta_tester=true,
// so they continue to see everything.
const navigation = [
  { name: "Dashboard", href: "/app/coach", icon: LayoutDashboard, exact: true },
  {
    name: "Articles",
    href: "/app/coach/articles",
    icon: FileText,
    subItems: [
      { name: "Articles", href: "/app/coach/articles" },
      { name: "Meetings", href: "/app/coach/meetings" },
    ],
  },
  // The Kingdom Factors book (#674): coaches only, beta until print exists.
  { name: "My Book", href: "/app/coach/books", icon: BookMarked, betaOnly: true, coachOnly: true },
  {
    name: "User Management",
    href: "/app/coach/users",
    icon: Users,
    subItems: [
      { name: "Users", href: "/app/coach/users" },
      { name: "Groups", href: "/app/coach/group" },
      { name: "Prospects", href: "/app/coach/prospects" },
    ],
  },
  {
    name: "Content",
    href: "/app/coach/content",
    icon: BookOpen,
    subItems: [
      { name: "Assigned", href: "/app/coach/content?tab=assigned" },
      { name: "All Tracks", href: "/app/coach/content?tab=tracks" },
      { name: "All Courses", href: "/app/coach/content?tab=courses" },
      { name: "My Courses", href: "/app/coach/content?tab=mine" },
    ],
  },
  {
    name: "Settings",
    href: "/app/coach/settings",
    icon: Settings,
    subItems: [
      { name: "Settings", href: "/app/coach/settings" },
      { name: "Tutorials", href: "/app/coach/tutorial_videos" },
    ],
  },
]

// NOTE: The coach onboarding tour does NOT auto-launch. It is only started by
// explicit user action via the "Take the tour ↺" button in the sidebar footer
// (which calls startTour() -> navigates to /app/coach?tour=1). Do not re-add a
// mount-time auto-start effect or mobile welcome modal here -- prior attempts
// created a loop that locked coaches out. See:
// docs/plans/2026-04-09-001-fix-disable-tour-auto-launch-plan.md
// docs/solutions/ui-bugs/tour-skip-race-condition-2026-04-02.md
function CoachLayoutInner({ children, currentUser }) {
  const { url } = usePage()
  const pathname = inertiaUrlPathname(url)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const isDesktop = useMediaQuery("(min-width: 1024px)")
  const { startTour } = useTour()

  useEffect(() => {
    if (isDesktop) setSidebarOpen(false)
  }, [isDesktop])

  const pathOnly = (href) => (href || "").split(/[?#]/)[0]

  const matchesPath = (href) => {
    const base = pathOnly(href)
    return pathname === base || pathname.startsWith(base + "/")
  }

  // "archived" (#649) has no sidebar sub-item; listing it here keeps the
  // sidebar from lighting All Courses while the Archived tab is open.
  const COACH_CONTENT_TABS = new Set(["assigned", "archived", "tracks", "courses", "mine"])

  // Both fallbacks must agree with `contentTabFromUrl` in
  // pages/App/Coach/Content/Index.jsx — a bare /app/coach/content lands on All
  // Courses (task #519). When they disagreed, the sidebar highlighted a
  // different sub-item than the tab the page had actually opened.
  const coachContentTabFromUrl = () => {
    const t = inertiaUrlTab(url)
    return t && COACH_CONTENT_TABS.has(t) ? t : "courses"
  }

  const coachContentTabFromHref = (href) => {
    const t = inertiaUrlTab(href)
    return t && COACH_CONTENT_TABS.has(t) ? t : "courses"
  }

  // A top-level item is active when it (or any of its subItems) prefixes the
  // current URL. `exact: true` forces strict equality so /app/coach doesn't
  // light up on every coach page.
  const isActive = (item) => {
    if (item.exact) return pathname === item.href
    const candidates = item.subItems ? item.subItems.map((s) => s.href) : [item.href]
    return candidates.some(matchesPath)
  }

  // Sub-item active = matches the URL with the longest matching prefix among
  // siblings. Prevents /coach/articles/123 from also lighting up the bare
  // /coach/articles tab when both are listed.
  const isSubActive = (item, sub) => {
    const subBase = pathOnly(sub.href)
    if (!matchesPath(subBase)) return false

    if (item.name === "Content") {
      return coachContentTabFromUrl() === coachContentTabFromHref(sub.href)
    }

    return !item.subItems.some(
      (other) =>
        other.href !== sub.href &&
        pathOnly(other.href).length > subBase.length &&
        matchesPath(other.href)
    )
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <ImpersonationBanner />
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-zinc-200 transform transition-transform lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between px-4 border-b border-zinc-200">
            <Link href="/" className="flex items-center gap-2">
              <img src={logoBlackText} alt="Kingdom Factor" className="h-8" />
            </Link>
            <button
              className="lg:hidden p-2 rounded-md hover:bg-zinc-100"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* User info */}
          <div className="px-4 py-3 border-b border-zinc-200">
            <p className="text-sm font-medium text-zinc-900">
              {currentUser?.name || currentUser?.email}
            </p>
            <p className="text-xs text-zinc-500 capitalize">
              {currentUser?.role} Portal
            </p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
            {navigation
              .filter((item) => !item.betaOnly || currentUser?.isBetaTester)
              .filter((item) => !item.coachOnly || currentUser?.role === "coach")
              .map((item) => {
              const active = isActive(item)
              const visibleSubItems = item.subItems?.filter(
                (s) => !s.betaOnly || currentUser?.isBetaTester
              )
              return (
                <div key={item.name}>
                  <Link
                    href={item.href}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      active
                        ? "bg-kf-red/10 text-kf-red"
                        : "text-zinc-700 hover:bg-zinc-100"
                    }`}
                  >
                    <item.icon className="h-5 w-5" />
                    {item.name}
                    {active && !item.subItems && (
                      <ChevronRight className="ml-auto h-4 w-4" />
                    )}
                  </Link>

                  {visibleSubItems && visibleSubItems.length > 0 && active && (
                    <div className="mt-1 ml-7 mb-1 space-y-1 border-l border-zinc-200 pl-3">
                      {visibleSubItems.map((sub) => {
                        const subActive = isSubActive(item, sub)
                        return (
                          <Link
                            key={sub.href}
                            href={sub.href}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                              subActive
                                ? "font-medium text-kf-red bg-kf-red/5"
                                : "text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
                            }`}
                          >
                            <span className="flex-1">{sub.name}</span>
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}

            {/* External CRM link */}
            <a
              href={CRM_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-tour="crm-link"
              className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-zinc-700 hover:bg-zinc-100 transition-colors"
            >
              <ExternalLink className="h-5 w-5" />
              CRM
              <ExternalLink className="ml-auto h-3 w-3 opacity-50" />
            </a>
          </nav>

          {/* Cross-portal links — only show portals the user can use. */}
          {(currentUser?.isMemberOfAnyGroup || currentUser?.isAdmin) && (
            <>
              <Separator className="mx-3" />
              <div className="px-3 py-4 space-y-1">
                {currentUser?.isMemberOfAnyGroup && (
                  <Link
                    href="/app/member"
                    className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-zinc-700 hover:bg-zinc-100 transition-colors"
                  >
                    <BookOpen className="h-5 w-5" />
                    Member Portal
                  </Link>
                )}
                {currentUser?.isAdmin && (
                  <Link
                    href="/app/admin"
                    className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-zinc-700 hover:bg-zinc-100 transition-colors"
                  >
                    <Shield className="h-5 w-5" />
                    Admin Portal
                  </Link>
                )}
              </div>
            </>
          )}

          {/* Footer */}
          <div className="p-3 border-t border-zinc-200 space-y-1">
            <button
              onClick={startTour}
              className="flex w-full items-center gap-3 px-3 py-2 rounded-md text-xs text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50 transition-colors"
            >
              Take the tour ↺
            </button>
            <Link
              href="/account"
              className="flex w-full items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-zinc-700 hover:bg-zinc-100 transition-colors"
            >
              <Lock className="h-5 w-5" />
              Account
            </Link>
            <Link
              href="/logout"
              method="post"
              as="button"
              className="flex w-full items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-zinc-700 hover:bg-zinc-100 transition-colors"
            >
              <LogOut className="h-5 w-5" />
              Sign Out
            </Link>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-zinc-200 flex items-center px-4 lg:px-6">
          <button
            className="lg:hidden p-2 rounded-md hover:bg-zinc-100 mr-4"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1" />
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">{children}</main>
      </div>

      <FeedbackWidget />
      {/* Tour overlay — only renders when isTourActive (driven by ?tour=N URL
          param, set only by the sidebar "Take the tour ↺" button) */}
      <CoachTour />
    </div>
  )
}

export function CoachLayout({ children, currentUser }) {
  return (
    <TourProvider>
      <CoachLayoutInner currentUser={currentUser}>
        {children}
      </CoachLayoutInner>
    </TourProvider>
  )
}
